import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import { EaveBracketSide } from "building-types/settings/roofBaseTypes";
import { SettingsType } from "building-types/settings/settingsType";
import { EaveType, FasciaTopTrimSize, ShutterType } from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { headCasingSlicesNew, headCasingStoneNew } from "../settingsTools";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultExteriorDoorCasingSettings,
  defaultExteriorWindowCasingSettings,
  defaultFarmhouseWindowSettings,
  defaultFriezeBoardSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultWainscotingSettings,
  defaultWindowSettings,
} from "./defaultSettings";

type WindowSettings = Pick<HomeSettings, "windowMullions" | "windowShutters" | "exteriorWindowCasing">;

const esperanzaWindowSettings: WindowSettings = {
  ...defaultFarmhouseWindowSettings,
  ...defaultWindowSettings,
  exteriorWindowCasing: {
    ...defaultExteriorWindowCasingSettings,
    ...headCasingSlicesNew(
      [
        { height: 6, depthOffset: 1, widthOffset: 0 },
        { height: 1, depthOffset: 1.15, widthOffset: 1 },
        { height: 1, depthOffset: 1.25, widthOffset: 1 },
      ],
      false,
    ),
    casingOrientation: StreetOrientationMode.FRONT_FACES,
  },

  windowShutters: { type: SettingsType.WINDOW_SHUTTER_SETTINGS, shutterType: ShutterType.DISABLED },
};

const esperanzaPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 240,
  doorPathColumnSpread: 60,
  columnId: undefined,
  includeRailing: false,
};

const esperanzaSettings: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.Esperanza_F2T]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Esperanza_F2T],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Esperanza_F2T + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Esperanza_F2T],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_4x4Pattern_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_4x4Pattern_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_Panel6_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_Panel6_01,
        },
      },
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 8,
      eaveType: EaveType.BOXED_MINI_HIP,
      eaveReturnDistance: 4,
      eaveExtensionAtCoplanarRake: 4,
      eaveExtensionAtNonCoplanarRake: 0,
      eaveExtensionAtCoplanarWall: 1.75,
      rakeExtensionAtCoplanarWall: 0,
      rakeBracketParams: [48],
      rakeBracketAtPeak: true,
      eaveBracketParamsNormalized: true,
      eaveBracketParams: [0.5],
      eaveBracketSide: EaveBracketSide.BOTH,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
      roofFramingSpacing: 24,
      lookoutRafterSpacing: 24,
      includeFlyRafters: true,
      includeLookoutRafters: true,
      includeStickFramingAtStart: true,
      webChordSpacingAtStart: 16,
      includeStickFramingAtEnd: true,
      webChordSpacingAtEnd: 16,
      setBackForStepDownTruss: 72,
      generateRoofFraming: false,
    },
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 4,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      thickness: 2.5,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
      capHeight: 1,
    },
    ...esperanzaWindowSettings,
    exteriorDoorCasing: {
      ...defaultExteriorDoorCasingSettings,
      ...headCasingStoneNew(6, 2),
    },
    porch: esperanzaPorchSettings,
  },
};

export default esperanzaSettings;
